import { useMemo } from 'react';
import { formatWhisperTooltip, WhisperTooltip } from './formatReportValuesUtils';
import { FormatActual, formatAmount, formatValue } from '../ValueFormatters/ValueFormatters';
import { Whisper, Tooltip } from 'rsuite';
import { Amounts } from 'types/report';
import { AskRequest, AskRequestStatus } from '@hone-automation/common';

export const useReportValueFormatters = ({
  cellData,
  smoothing,
  showAnomaly,
  showtrxAnomaly,
  composedAmount,
  flaggedCell,
  flagInfo,
  filters,
}: {
  cellData: Amounts;
  smoothing: boolean;
  showAnomaly: boolean;
  showtrxAnomaly: boolean;
  composedAmount: number | null;
  flaggedCell: AskRequestStatus | undefined;
  flagInfo: {
    flaggedTransactions: AskRequest[];
    isFlaggedCell: boolean | undefined;
    fTrxtotal: number;
    totalTrxFlagged: number;
  };
  filters: any;
}) => {
  const { transactions, amount, amountAVG, varianceAnalisys, vizHelpers, anomaly, trxAnomaly, dataRows } = cellData;

  const formatters = useMemo(
    () => ({
      // variable tooltip for actuals
      formatVariableActualTooltip: () => {
        const tooltipData: WhisperTooltip[] = [];
        const trxFlagged = cellData.trxFlagged;
        // Use transaction property to distinguish: items with transaction are trx-level, without are cell-level
        const flaggedTransactions = flaggedCell && trxFlagged.some(trx => trx.transaction);
        const isFlaggedCell = flaggedCell && trxFlagged.some(trx => !trx.transaction);
        const deviation = amount !== null ? amount - anomaly.stats?.mean : 0;
        const mustSmooth = smoothing && !vizHelpers.noValuesToShow && amountAVG !== undefined && amountAVG !== amount;

        // Flagged Cell info - find the actual cell-level flag (not transaction-level)
        const cellLevelFlag = trxFlagged.find(trx => !trx.transaction);
        if (flagInfo.isFlaggedCell && cellLevelFlag) {
          tooltipData.push(
            {
              title: 'Flagged Cell',
              value: formatAmount(cellLevelFlag.flaggedAmount),
              style: {
                textTransform: 'uppercase',
                fontSize: '10px',
                textAlign: 'center',
                paddingTop: '5px',
                fontWeight: 'bold',
                color: flaggedCell === 'Open' ? 'var(--ks-ASK-customer-text)' : 'var(--ks-ASK-resolved-text)',
              },
              valueStyle: { fontSize: '11px' },
            }
            /* { title: 'Value', value: formatAmount(trxFlagged[0].amount), style: { paddingBottom: '5px' } } */
          );
        }
        // Smoothing info
        if (mustSmooth) {
          tooltipData.push({
            title: 'Original',
            value: formatAmount(amount),
            style: { paddingBottom: '5px', marginTop: '5px', borderBottom: '1px solid #444' },
            valueStyle: { color: 'var(--ks-smoothing-color-text)' },
          });
        }
        // Transactions info
        if (transactions && transactions > 0) {
          // Only display the amount of transactions if there are more than one transaction
          if (transactions > 0) {
            // Review all transactions & calculate total
            const trxtotal = dataRows.reduce((total, row) => total + row.amount, 0);
            // Add total if there is moore than one transaction
            tooltipData.push({
              title: `${transactions} transaction${transactions && transactions > 1 ? 's' : ''}`,
              value: formatAmount(trxtotal),
              style: { marginTop: '5px' },
            });
          }
          if (flagInfo.flaggedTransactions) {
            const fTrxtotal = flagInfo.fTrxtotal;
            const totalTrxFlagged = flagInfo.totalTrxFlagged;
            if (totalTrxFlagged > 0) {
              tooltipData.push({
                title: `${totalTrxFlagged} Flagged trx${trxFlagged && totalTrxFlagged > 1 ? 's' : ''}`,
                value: formatAmount(fTrxtotal),
                style: {
                  textTransform: 'uppercase',
                  fontSize: '10px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: flaggedCell === 'Open' ? 'var(--ks-ASK-customer-text)' : 'var(--ks-ASK-resolved-text)',
                },
                valueStyle: { fontSize: '11px' },
              });
            }
          }
        }
        // Click Message
        tooltipData.push({
          title:
            amount && transactions && transactions > 0
              ? 'Click to flag or review transactions'
              : flaggedCell && !flagInfo.flaggedTransactions
                ? 'Click to review the Flagged item'
                : amount
                  ? 'Click to flag this Value'
                  : 'Click to flag this cell',
          value: '',
          style: { opacity: 0.7, width: '100%', marginBottom: '5px', fontStyle: 'italic' },
        });
        // Average info
        if (amount) {
          tooltipData.push({
            title: `AV ${formatAmount(anomaly.stats?.mean)}`,
            value: `𝚫 ${formatAmount(deviation)}`,
            style: {
              opacity: 0.8,
              width: '100%',
              textTransform: 'uppercase',
              fontSize: '10px',
              fontWeight: 'bold',
              marginBottom: '5px',
              paddingTop: '5px',
              borderTop: '1px solid #444',
            },
            valueStyle: {
              color:
                deviation > 0
                  ? 'var(--ks-anomaly-positive-contrast-color)'
                  : 'var(--ks-anomaly-negative-contrast-color)',
            },
          });
        }
        return tooltipData;
      },
      // Formatters will be added here
      formatComposedAmount: (
        budget: number | null = null,
        small: boolean = false,
        transactionCount?: number,
        cb?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
      ) => {
        const auditResult = cellData.auditResult;
        const shouldShowAuditAsBS =
          auditResult && ['warning', 'error', 'fail', 'dismissed'].includes(auditResult.type?.toLowerCase() || '');
        // Let's get the tooltip for the actuals
        const formatVariableActualTooltip = budget ? [] : formatters.formatVariableActualTooltip();
        if (formatVariableActualTooltip.length > 0) {
          const tooltip = formatWhisperTooltip(formatVariableActualTooltip);
          return (
            <Whisper placement="right" trigger="hover" delayOpen={600} speaker={<Tooltip>{tooltip}</Tooltip>}>
              {FormatActual(
                budget ?? composedAmount,
                vizHelpers.isTotal,
                small,
                shouldShowAuditAsBS,
                auditResult,
                transactionCount,
                cb
              )}
            </Whisper>
          );
        }

        return FormatActual(
          budget ?? composedAmount,
          vizHelpers.isTotal,
          small,
          shouldShowAuditAsBS,
          auditResult,
          transactionCount,
          cb
        );
      },

      formatVariance: (children: React.ReactElement) => {
        // stats info
        if (varianceAnalisys && varianceAnalisys.tendency !== 'neutral') {
          const tooltip =
            amount !== null &&
            formatWhisperTooltip([
              {
                title: varianceAnalisys.category,
                value: varianceAnalisys.tendency,
                style: { padding: '10px 0', fontWeight: 'bold' },
                valueStyle: {
                  color:
                    varianceAnalisys.tendency === 'positive'
                      ? 'var(--ks-anomaly-positive-contrast-color)'
                      : 'var(--ks-anomaly-negative-contrast-color)',
                },
              },
              { title: varianceAnalisys.message, value: '', style: { opacity: 0.7 } },
              {
                title: varianceAnalisys.explanation || '',
                value: '',
                style: { fontSize: '12px', padding: '10px 0', color: 'var(--ks-white)' },
              },
            ]);
          return (
            <Whisper placement="top" trigger="hover" delayOpen={400} speaker={<Tooltip>{tooltip}</Tooltip>}>
              {children}
            </Whisper>
          );
        }
        return children;
      },

      formatActualsBlock: (children: React.ReactElement) => {
        // stats info
        if (showAnomaly) {
          const deviation = amount !== null ? amount - anomaly.stats?.mean : 0;
          const tooltip =
            amount !== null &&
            formatWhisperTooltip([
              { title: 'Average', value: formatAmount(anomaly.stats?.mean) },
              {
                title: 'Deviation',
                value: formatAmount(deviation),
                valueStyle: {
                  color:
                    deviation > 0
                      ? 'var(--ks-anomaly-positive-contrast-color)'
                      : 'var(--ks-anomaly-negative-contrast-color)',
                },
              },
            ]);
          return (
            <Whisper placement="top" trigger="hover" delayOpen={800} speaker={<Tooltip>{tooltip}</Tooltip>}>
              {children}
            </Whisper>
          );
        }
        return children;
      },
    }),
    [
      filters.smoothing,
      flagInfo.flaggedTransactions,
      showAnomaly,
      showtrxAnomaly,
      composedAmount,
      flaggedCell,
      amount,
      amountAVG,
      anomaly,
      trxAnomaly,
      varianceAnalisys,
      vizHelpers,
      transactions,
      dataRows,
      cellData.trxFlagged,
    ]
  );

  return formatters;
};
